import { combineReducers } from "redux";

import containersReducer from "../reducers/containersReducer";
import itemsReducer from "./itemsReducer";
import configurationReducer from "./configurationsReducer";
import artworkReducer from "./artworkReducer";
import reportsReducer from "./reportsReducer";
import operationsReducer from "./operationsReducer";
import hubspotReducer from "./hubspotReducer";
import adminReducer from "./adminReducer";
import productTrackerReducer from "./productTrackerReducer";
import chinaTrackerReducer from "./chinaTrackerReducer";
import notificationReducer from "./notificationReducer";
import usersReducer from "./userReducer";
import portalReducer from "./portalReducer";
import { deliveryReducer, stockTransferReducer } from "./deliveryReducer";
import persistReducer from "redux-persist/es/persistReducer";
import AsyncStorage from "@react-native-async-storage/async-storage";

// Create a separate persist configuration for the admin reducer only
const persistConfig = {
  key: "admin", // Persist only the admin state
  storage: AsyncStorage,
};

const CONFIGURATIONS_SCHEMA_VERSION = 2;

const configurationsPersistConfig = {
  key: "configurations",
  storage: AsyncStorage,
  version: CONFIGURATIONS_SCHEMA_VERSION,
  migrate: (state: any) => {
    // No persisted state at all (fresh install/first login) — nothing to do.
    if (!state) return Promise.resolve(state);

    const persistedVersion = state._persist?.version ?? -1;

    if (persistedVersion < CONFIGURATIONS_SCHEMA_VERSION) {
      // Discard the stale blob entirely, same as the LOGOUT action does at
      // the root level (state = undefined) — falls through to
      // configurationReducer's own default initial state.
      return Promise.resolve(undefined);
    }

    return Promise.resolve(state);
  },
  // isUserAuthorized is deliberately NOT persisted. It is not a preference,
  // it is a claim about the current session, and the only code that verifies
  // that claim (RootTabNavigator's bootstrap effect) is guarded by
  // `if (!isUserAuthorized)`. Persisting it true meant a reload after the
  // token expired booted straight past that check into an authorized shell
  // with no valid token, with no way back to the login screen. Always start
  // false so the token is re-checked on every boot.
  whitelist: [
    "userInfo",
    "currentCompany",
    "currentRole",
    "userRoles",
    "logo",
    "language",
    "themeMode",
  ],
  blacklist: ["navigation"],
};
const persistedAdminReducer = persistReducer(persistConfig, adminReducer);
const persistedConfigurationReducer = persistReducer(
  configurationsPersistConfig,
  configurationReducer,
);

const appReducer = combineReducers({
  configurations: persistedConfigurationReducer,
  containers: containersReducer,
  items: itemsReducer,
  delivery: deliveryReducer,
  stockTransfer: stockTransferReducer,
  artwork: artworkReducer,
  reports: reportsReducer,
  operations: operationsReducer,
  hubspot: hubspotReducer,
  admin: persistedAdminReducer,
  productTracker: productTrackerReducer,
  chinaTracker: chinaTrackerReducer,
  notification: notificationReducer,
  users: usersReducer,
  portal: portalReducer,
});

//  Reset Redux state on logout
const rootReducer = (state, action) => {
  if (action.type === "LOGOUT") {
    state = undefined; // This resets all slices to their initial state
  }
  return appReducer(state, action);
};
export default rootReducer;
